import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Button } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { $contacts } from '@features/socket-chats-reworked/models';
import {
  $forwardedMessages,
  clearForwardedMessages,
} from '@features/socket-chats-reworked/models/chat/chat.model';
import { MessageInstance } from '@features/socket-chats-reworked/types/common';
import { getSingleOrPluralNoun } from './helpers';
import { useStyles } from './styles';

const ONE_MESSAGE = 1;

export const ForwardMessages = () => {
  const classes = useStyles({ isHidden: false });
  const [forwardedMessages] = useUnit([$forwardedMessages]);

  if (!forwardedMessages?.length) return null;

  return (
    <div className={classes.message_wrap}>
      {forwardedMessages?.length === ONE_MESSAGE && (
        <OneForwardMessage data={forwardedMessages[0]} />
      )}

      {forwardedMessages?.length > ONE_MESSAGE && (
        <ManyForwardMessage data={forwardedMessages} />
      )}

      <Button
        variant="text"
        onClick={() => {
          clearForwardedMessages();
        }}
      >
        <CloseIcon />
      </Button>
    </div>
  );
};

const ManyForwardMessage = ({ data }: { data: MessageInstance[] }) => {
  const classes = useStyles({ isHidden: false });
  const { t } = useTranslation();
  const contacts = useUnit($contacts);

  const forwardedContact = contacts[data[0].contact_id];
  const name = forwardedContact?.name || t('Anonymous');
  const address = forwardedContact?.meta?.address?.title;

  const allContacts = data.map(({ contact_id }) => contact_id);
  const unicContacts = Array.from(new Set(allContacts));

  return (
    <div className={classes.message}>
      <div className={classes.message__userTitle}>
        {name}{' '}
        {unicContacts.length > 1
          ? getSingleOrPluralNoun(
              unicContacts.length - 1,
              `${t('andMore')} ${unicContacts.length - 1} ${t('UserSingle')}`,
              `${t('andMore')} ${unicContacts.length - 1} ${t('UserFew')}`,
              `${t('andMore')} ${unicContacts.length - 1} ${t('UserMany')}`
            )
          : ''}
      </div>

      {address && unicContacts.length === 1 && (
        <div className={classes.message__userAddress}>{address}</div>
      )}

      <div className={classes.message__userText}>
        {data.length}{' '}
        {getSingleOrPluralNoun(
          data.length,
          t('Message'),
          t('navMenu.informationCenter.messages'),
          t('messages')
        )}
      </div>
    </div>
  );
};

const OneForwardMessage = ({ data }: { data: MessageInstance }) => {
  const classes = useStyles({ isHidden: false });
  const { t } = useTranslation();
  const contacts = useUnit($contacts);

  const forwardedContact = contacts[data.contact_id];
  const name = forwardedContact?.name || t('Anonymous');
  const address = forwardedContact?.meta?.address?.title;

  if (data.type === null) {
    return (
      <div className={classes.message}>
        <div className={classes.message__userTitle}>{name}</div>

        {address && <div className={classes.message__userAddress}>{address}</div>}

        <div className={classes.message__userText}>{data.text}</div>
      </div>
    );
  }

  return (
    <div className={classes.message}>
      <div className={classes.message__userTitle}>{name}</div>

      {address && <div className={classes.message__userAddress}>{address}</div>}

      <div className={classes.message__userText}>
        <div className={classes.message__icon}>
          <AttachFileIcon />
        </div>
        {data.type === 'image' ? t('Image') : t('File')}
      </div>
    </div>
  );
};
